import { el, formatDate, mount, onTap } from '../ui/dom.ts';
import { iconEl } from '../ui/icons.ts';
import { api, ApiError } from '../api.ts';
import { confirmDialog, toast } from '../ui/feedback.ts';
import { navigate } from '../router.ts';
import { loadSession, state } from '../state.ts';
import { isCar } from '../ui/surface.ts';
import { t } from '../i18n/index.ts';
import { withContactLink } from '../ui/contact.ts';

// Fiyat bileşeni TEK: landing de, uygulama içindeki Paketler ekranı da bunu
// kullanır. Böylece fiyat, yenileme metni ve test ödeme işareti iki yerde
// ayrışamaz.
//
// Değişmez kurallar (10-TASARIM-V2 + 07-TICARI-MODEL):
//  - İlk ay ücreti ile sonraki yenileme HER ZAMAN birlikte görünür.
//  - Kart başına en fazla 3–4 kısa özellik. Geliştirme notu kartı doldurmaz.
//  - "Sınırsız / 4K / reklamsız" gibi doğrulanmamış vaat yok.
//  - Test ödemesi işareti ödeme EYLEMİNDE görünür, kartı kaplamaz.
//
// Paket adı, slogan ve maddeler sunucudan seçili dilde gelir (X-Lang başlığı).

export interface PlanView {
  code: string;
  name: string;
  tagline: string;
  bullets: string[];
  exclusions: string[];
  firstLabel: string;
  renewLabel: string;
  /** Yalnızca sayı kısmı (ör. "49,90"); para birimi ve dönem arayüzde yazılır. */
  firstAmount: string;
  introApplied: boolean;
  /** Ücretli pakete ilk kez geçen hesaba tanınan deneme süresi (gün); yoksa 0. */
  trialDays?: number;
  iptv: boolean;
  concurrentStreams: number;
  dailySeconds: number | null;
  monthlyHoursCap: number | null;
  maxHeight: number | null;
  sourceSlots: number;
  monthlyMinor: number;
  /** Yıllık ödeme: tutar, aylık karşılığı ve kazanç sunucudan hazır gelir. */
  yearlyAmount: string;
  yearlyListAmount?: string;
  yearlyLabel?: string;
  yearlyPerMonthLabel: string;
  yearlySavingLabel: string;
  yearlyFreeMonths: number;
}

export type BillingPeriod = 'monthly' | 'yearly';

export interface PlansPayload {
  currentPlan: string | null;
  introOfferUsed: boolean;
  plans: PlanView[];
  addons: Array<{ code: string; name: string; description: string; requiresPlan: string; priceLabel: string; sellable: boolean }>;
  payments: { provider: string; mock: boolean; enabled: boolean; sandbox?: boolean };
  /** Sonucu henüz alınamamış bir ödeme formu var; arayüz yeniden sorar. */
  pendingCheckout?: boolean;
}

export interface PlanSectionOptions {
  context: 'landing' | 'app';
  onChanged?: () => void;
  /** Araçta satın alma telefona devredilir. Kullanıcı "yine de burada devam
   *  et" derse bu bayrak gelir ve normal ödeme akışı açılır — yolu kapatmıyoruz. */
  forceCheckoutHere?: boolean;
}

export function planSection(data: PlansPayload, options: PlanSectionOptions): HTMLElement {
  // Landing oturumdan bağımsız: "mevcut paketiniz" işareti ve kişisel
  // düğmeler yalnız uygulama içinde.
  const loggedIn = options.context === 'app' && !!state.session;
  if (options.context === 'landing') data = { ...data, currentPlan: null };
  // Dönem seçimi kartların üstünde durur ve kartları yerinde yeniden çizer.
  let period: BillingPeriod = loggedIn && state.session?.subscription?.period === 'yearly' ? 'yearly' : 'monthly';
  const stripHost = el('div', {});
  const draw = () => mount(stripHost, plansStrip(data, options, loggedIn, period));
  const switcher = periodSwitch(() => period, (next) => { period = next; draw(); });
  draw();
  return el('div', {},
    // Uygulama içindeki Paketler ekranının alt başlığı zaten "Her pakette aynı
    // anda tek yayın" diyor; bölüm girişi aynı cümleyi tekrar ediyordu.
    options.context === 'landing'
      ? el('div', { class: 'section-intro' },
          el('h2', { class: 'section-title' }, t('plans.intro.title1'), el('br'), t('plans.intro.title2')),
          el('p', {}, t('plans.intro.body1'), el('br'), t('plans.intro.body2')),
        )
      : null,
    switcher,
    stripHost,
    el('p', { class: 'fine' },
      t('plans.fine.vat'), ' ', t('plans.fine.limits'), ' ',
      el('br'),
      t('plans.fine.noIptv'), ' ',
      el('span', {}, ...(!data.payments.enabled ? withContactLink('plans.fine.offline') : [data.payments.mock ? t('plans.fine.mock') : t('plans.fine.real')])),
      data.payments.enabled && data.payments.sandbox
        ? el('span', {}, el('br'), el('span', { class: 'tag-soft tag-test' }, 'TEST'), ' ', t('plans.fine.sandbox'))
        : null,
    ),
  );
}

/**
 * Telefonda kartlar yan yana kaydırılır (istenen davranış). Tek eksik şey
 * kaydırılabildiğinin belli olmasıydı: kullanıcı yanda kart olduğunu ancak
 * kenardan kesilmiş bir harfle anlıyordu. Noktalar bunu söylüyor ve hangi
 * kartta olduğunu gösteriyor. Geniş ekranda üçü yan yana durduğu için gizli.
 */
function plansStrip(data: PlansPayload, options: PlanSectionOptions, loggedIn: boolean, period: BillingPeriod): HTMLElement {
  const strip = el('div', { class: 'plans' },
    ...data.plans.map((plan) => planCard(plan, data, options, loggedIn, period)),
  );
  const dots = el('div', { class: 'plans-hint' },
    ...data.plans.map((plan, index) => el('i', { class: index === 0 ? 'on' : '', title: plan.name })),
    el('span', {}, t('plans.swipe')),
  );
  strip.addEventListener('scroll', () => {
    const step = strip.scrollWidth / Math.max(1, data.plans.length);
    const index = Math.round(strip.scrollLeft / step);
    dots.querySelectorAll('i').forEach((dot, i) => dot.classList.toggle('on', i === index));
  }, { passive: true });
  return el('div', {}, strip, dots);
}

/** Aylık / yıllık seçimi. Yıllığın kazancı düğmenin üstünde yazar: seçmeden
 *  önce neden seçileceği görünsün. */
function periodSwitch(current: () => BillingPeriod, onPick: (period: BillingPeriod) => void): HTMLElement {
  const make = (period: BillingPeriod, label: string, badge?: string) => {
    const button = el('button', { type: 'button', 'aria-pressed': 'false' },
      el('span', {}, label), badge ? el('em', {}, badge) : null);
    onTap(button, () => { onPick(period); paint(); });
    return { period, button };
  };
  const items = [
    make('monthly', t('plans.period.monthly')),
    make('yearly', t('plans.period.yearly'), t('plans.period.badge')),
  ];
  const paint = () => {
    for (const item of items) {
      const on = item.period === current();
      item.button.classList.toggle('active', on);
      item.button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };
  paint();
  return el('div', { class: 'period-switch', role: 'group', 'aria-label': t('plans.period.label') },
    ...items.map((item) => item.button));
}

function planCard(plan: PlanView, data: PlansPayload, options: PlanSectionOptions, loggedIn: boolean, period: BillingPeriod): HTMLElement {
  const isCurrent = data.currentPlan === plan.code;
  const action = buildAction(plan, isCurrent, options, loggedIn, data, period);
  const yearly = period === 'yearly' && plan.monthlyMinor > 0;

  return el('article', { class: 'plan' + (plan.code === 'plus' ? ' is-plus' : '') },
    el('h3', {}, plan.name),
    el('p', { class: 'desc' }, plan.tagline),
    el('div', { class: 'amount' },
      yearly && plan.yearlyListAmount ? el('s', { class: 'was' }, plan.yearlyListAmount) : null,
      plan.monthlyMinor === 0 ? t('plans.free') : yearly ? plan.yearlyAmount : plan.firstAmount,
      plan.monthlyMinor === 0 ? null
        : el('small', {}, yearly ? t('plans.perYear') : plan.introApplied ? t('plans.perFirstMonth') : t('plans.perMonth')),
    ),
    // İlk ay ve sonraki yenileme birlikte; yıllıkta aylık karşılığı ve kazanç.
    el('p', { class: 'renewal' }, ...(yearly
      ? [plan.trialDays
          ? t('plans.renew.yearlyTrial', { n: plan.trialDays, price: plan.yearlyPerMonthLabel })
          : t('plans.renew.yearly', { price: plan.yearlyPerMonthLabel }), el('br'),
         el('span', { class: 'save' }, t('plans.renew.yearlyNote', { n: plan.yearlyFreeMonths, saving: plan.yearlySavingLabel }))]
      : renewalText(plan))),
    el('ul', {},
      ...plan.bullets.slice(0, 4).map((line) =>
        el('li', {}, iconEl('check'), el('span', {}, line))),
      // Olumsuz madde tikle listelenince "IPTV var" diye okunuyordu.
      ...(plan.exclusions ?? []).map((line) =>
        el('li', { class: 'no' }, iconEl('minus'), el('span', {}, line))),
    ),
    action,
  );
}

function renewalText(plan: PlanView): Array<string | Node> {
  if (plan.monthlyMinor === 0) return [t('plans.renew.free')];
  if (plan.trialDays) {
    return [t('plans.renew.trial', { n: plan.trialDays }), el('br'), t('plans.renew.trialNote', { price: plan.renewLabel })];
  }
  if (plan.introApplied) {
    return [t('plans.renew.intro', { price: plan.renewLabel }), el('br'), t('plans.renew.introNote')];
  }
  return [t('plans.renew.flat'), el('br'), t('plans.renew.flatNote')];
}

function buildAction(plan: PlanView, isCurrent: boolean, options: PlanSectionOptions, loggedIn: boolean, data?: PlansPayload, period: BillingPeriod = 'monthly'): HTMLElement {
  const klass = plan.code === 'free' ? 'btn btn-line' : 'btn btn-dark';

  // "Etkin paket" devre dışı bir düğme gibi duruyordu: "kullanılamaz" ile
  // "zaten sende" ayrımı ilk bakışta yoktu. Artık tıklanabilir görünmüyor.
  if (isCurrent) {
    // İptal edilmiş abonelik sağlayıcıda geri açılamıyor; metin "yeniden abone
    // olabilirsiniz" diyordu ama kart yalnız "mevcut paketiniz" gösteriyordu.
    const own = state.session?.entitlements;
    if (loggedIn && plan.code !== 'free' && data?.payments.enabled && data.payments.provider === 'iyzico'
        && own?.cancelAtPeriodEnd && own.canResume === false && !(isCar() && !options.forceCheckoutHere)) {
      const again = el('button', { class: 'btn btn-line', type: 'button' }, t('plans.resubscribe'));
      onTap(again, () => void goToCheckout(plan.code, period));
      return el('div', { class: 'plan-current' }, el('p', { class: 'current-mark' }, iconEl('check'), t('plans.current')), again);
    }
    return el('p', { class: 'current-mark' }, iconEl('check'), t('plans.current'));
  }
  if (plan.code === 'free') {
    // Ücretli pakettekine "Ücretsiz devam et" diye etkin bir düğme göstermek
    // belirsizdi: düşürme mi, iptal mi, hiçbir şey mi? Alt pakete geçiş
    // abonelik iptaliyle olur; düğme yerine bunu söylüyoruz.
    if (loggedIn && data?.currentPlan && data.currentPlan !== 'free') {
      return el('p', { class: 'plan-note' }, t('plans.backToFree'));
    }
    const button = el('button', { class: klass, type: 'button' }, loggedIn ? t('plans.continueFree') : t('plans.startFree'));
    onTap(button, () => navigate(loggedIn ? '/browse' : '/login?mode=signup'));
    return button;
  }
  // Çevrim içi ödeme kapalıyken 503 veren bir düğme yerine geçiş yolunu söylüyoruz.
  if (data && !data.payments.enabled) {
    // Tanıtım sayfasında ücretli kart çıkmaz sokak olmasın: hesap açılır.
    // Geçiş yolu hesap açıldıktan sonra Paketler ekranında yazar.
    if (!loggedIn) {
      const signup = el('button', { class: klass, type: 'button' }, t('plans.createAccount'));
      onTap(signup, () => navigate(`/login?mode=signup&plan=${plan.code}`));
      return signup;
    }
    return el('p', { class: 'plan-note' }, ...withContactLink('plans.contactToUpgrade'));
  }
  // Sağlayıcıda tahsilatı süren abonelik varken ikincisi açılmaz (çift ücret
  // olurdu); form doldurulduktan sonra hata vermek yerine burada söylüyoruz.
  // Sunucuyla aynı ölçüt: tahsilatı süren ya da ödenmemiş abonelik. Eskiden
  // geçerli pakete bakılıyordu; paketi düşmüş ama aboneliği ödenmemiş kalan
  // müşteri formu doldurduktan sonra "önce iptal edin" hatası alıyordu.
  const ent = state.session?.entitlements;
  const sub = state.session?.subscription;
  const billed = sub ? sub.status === 'active' || sub.status === 'unpaid' : !!ent && ent.effectivePlan !== 'free' && !ent.cancelAtPeriodEnd;
  if (loggedIn && data?.payments.provider === 'iyzico' && billed) {
    if (sub?.paymentFailed) return el('p', { class: 'plan-note' }, t('plans.unpaidFirst'));
    // Alt pakete geçiş dönem sonunda uygulanır; o güne dek yeni bir değişiklik yapılmaz.
    if (sub?.pendingPlan) {
      return el('p', { class: 'plan-note' }, sub.pendingPlan.code === plan.code
        ? t('plans.changePendingThis', { date: formatDate(sub.nextChargeAt) })
        : t('plans.changePending', { name: sub.pendingPlan.name }));
    }
    // Aynı ödeme dönemi içinde paket değiştirilebilir (iyzico dönem değişikliğini reddediyor).
    if (sub?.canChangePlan && sub.period === period && data) {
      const change = el('button', { class: klass, type: 'button' }, t('plans.changeTo', { name: plan.name }));
      onTap(change, () => void changePlan(plan, data, period, options));
      return change;
    }
    return el('p', { class: 'plan-note' }, t(sub && sub.period && sub.period !== period ? 'plans.periodChange' : 'plans.cancelFirst'));
  }
  // Araç ekranında ödeme adımını açmıyoruz: kart/klavye işi telefona ait.
  if (isCar() && !options.forceCheckoutHere) {
    const button = el('button', { class: klass, type: 'button' }, iconEl('phone'), t('plans.buyOnPhone'));
    onTap(button, () => navigate(`/handoff?task=plan&plan=${plan.code}&period=${period}`));
    return button;
  }
  const button = el('button', { class: klass, type: 'button' }, t('plans.choose', { name: plan.name }));
  onTap(button, () => {
    if (!loggedIn) { navigate(`/login?mode=signup&plan=${plan.code}`); return; }
    // iyzico aboneliği kişiye açar: önce fatura bilgisi, sonra iyzico'nun formu.
    if (data?.payments.provider === 'iyzico') {
      void goToCheckout(plan.code, period);
      return;
    }
    void startCheckout(plan, options, period);
  });
  return button;
}

/**
 * Paket değişikliği. Sonuç, onaydan ÖNCE tam olarak söylenir: deneme süresinde
 * paket hemen değişir ve ilk tahsilat yeni fiyattan yapılır; ücreti alınmış
 * abonelikte üst paket hemen açılır ve bu dönem için ek ücret alınmaz, alt pakete
 * geçiş ise dönem sonunda uygulanır.
 */
async function changePlan(plan: PlanView, data: PlansPayload, period: BillingPeriod, options: PlanSectionOptions): Promise<void> {
  const sub = state.session?.subscription;
  if (!sub) return;
  const order = data.plans.map((p) => p.code);
  const upgrade = order.indexOf(plan.code) > order.indexOf(data.currentPlan ?? '');
  const price = period === 'yearly' ? plan.yearlyLabel ?? plan.renewLabel : plan.renewLabel;
  const date = formatDate(sub.nextChargeAt);
  const ok = await confirmDialog({
    title: t('plans.changeTitle', { name: plan.name }),
    body: t(sub.inTrial ? 'plans.changeTrial' : upgrade ? 'plans.changeUp' : 'plans.changeDown', { name: plan.name, date, price }),
    confirmLabel: t('plans.changeConfirm'),
  });
  if (!ok) return;
  try {
    const result = await api.post<{ effective: 'now' | 'next_period' }>('/api/billing/change-plan', { planCode: plan.code });
    await loadSession();
    toast(result.effective === 'now' ? t('plans.changedNow', { name: plan.name }) : t('plans.changedLater', { date }), 'ok', 7000);
    options.onChanged?.();
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('plans.checkout.fail'), 'error');
  }
}

/** Süresi dolmamış (iptal edilmiş) bir paket varken yeni abonelik: ücret bugün
 *  alınır ve mevcut dönemin kalan süresi eklenmez. Bu, form doldurulmadan önce söylenir. */
async function goToCheckout(planCode: string, period: BillingPeriod): Promise<void> {
  const ent = state.session?.entitlements;
  if (ent && ent.effectivePlan !== 'free' && ent.renewsAt && ent.renewsAt > Date.now()) {
    const ok = await confirmDialog({
      title: t('plans.switchTitle'),
      body: t('plans.switchBody', { date: formatDate(ent.renewsAt) }),
      confirmLabel: t('plans.switchConfirm'),
    });
    if (!ok) return;
  }
  navigate(`/checkout/details?plan=${planCode}&period=${period}`);
}

export async function startCheckout(plan: { code: string; name: string }, options: PlanSectionOptions, period: BillingPeriod = 'monthly'): Promise<void> {
  try {
    const checkout = await api.post<{
      checkoutId: string; mock: boolean;
      summary: { planName: string; firstLabel: string; renewLabel: string; note: string; introApplied: boolean; period?: BillingPeriod };
    }>('/api/billing/checkout', { planCode: plan.code, period });

    // Fiyat üç kez tekrar ediyordu (başlık, ilk ödeme, yenileme). Kampanya
    // yoksa ilk ödeme ile yenileme zaten aynı; tek satır yeter.
    const body = el('div', { style: { display: 'grid', gap: '10px' } },
      el('p', {}, checkout.summary.note),
      el('p', {}, checkout.summary.period === 'yearly'
        ? t('plans.checkout.yearly', { price: checkout.summary.firstLabel })
        : checkout.summary.introApplied
        ? t('plans.checkout.first', { first: checkout.summary.firstLabel, renew: checkout.summary.renewLabel })
        : t('plans.checkout.monthly', { price: checkout.summary.firstLabel })),
      checkout.mock
        ? el('p', {}, el('span', { class: 'tag-soft tag-test' }, 'TEST'), ' ', t('plans.checkout.test'))
        : null,
    );

    const ok = await confirmDialog({
      title: t('plans.checkout.title', { name: checkout.summary.planName }),
      body,
      confirmLabel: checkout.mock ? t('plans.checkout.confirmTest') : t('plans.checkout.confirm'),
    });
    if (!ok) return;
    navigate(`/checkout/test?checkout=${encodeURIComponent(checkout.checkoutId)}&plan=${plan.code}&period=${period}`);
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('plans.checkout.fail'), 'error');
  }
}
